import { useEffect, useState } from 'react';
import { Box, Table, Td, Tr } from '@chakra-ui/react';
import { Colors, Text } from '@mercator-gcp/grout';
import { Spinner } from 'components/elements/Spinner';
import StreamingText from 'components/elements/StreamingText';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import LoadingStateThinking from 'components/widgets/ProjectDetails/GenerateDetailsWithAI/LoadingState';
import { useGenerateProjectDetails } from 'hooks/useAIInteractionRequest';

const loadingStyle = {
  alignItems: 'center',
  gap: 'padding_8',
  paddingTop: 'padding_8',
};

const aiDisplayStyle = {
  maxHeight: '600px',
  overflowY: 'auto',
};

const tableStyle = {
  'th, td': {
    border: 'none',
  },
};

const aiSectionHeaderStyle = {
  padding: 'padding_8',
  alignItems: 'top',
  verticalAlign: 'top',
  color: Colors.TextQuiet,
};

const aiSectionBodyStyle = {
  padding: 'padding_8',
  textAlign: 'left',
  width: '100%',
};

type RequestDiplayGatorBotProps = {
  projectSlug?: string;
  documentId?: string;
};

const Section = ({
  title,
  content,
  shouldStream = true,
}: {
  title: string;
  content: string;
  shouldStream?: boolean;
}) => (
  <Tr>
    <Td sx={aiSectionHeaderStyle}>
      <Text.SmallLabel>{title}</Text.SmallLabel>
    </Td>
    <Td sx={aiSectionBodyStyle}>
      <StreamingText text={content} speed={shouldStream ? 20 : 0} />
    </Td>
  </Tr>
);

const RequestDiplayGatorBot = ({
  projectSlug,
  documentId,
}: RequestDiplayGatorBotProps) => {
  const { data, isLoading } = useGenerateProjectDetails({ projectSlug, documentId });
  const [shouldStartStreaming, setShouldStartStreaming] = useState(false);

  // Wait for loading to complete before starting streaming animation
  useEffect(() => {
    if (!isLoading && data?.text) {
      // Small delay to ensure smooth transition from loader to streaming
      const timer = setTimeout(() => {
        setShouldStartStreaming(true);
      }, 100);

      return () => clearTimeout(timer);
    }
    if (isLoading) {
      setShouldStartStreaming(false);
    }

    return undefined;
  }, [isLoading, data?.text]);

  if (isLoading) {
    return (
      <FlexColumn sx={loadingStyle}>
        <Text.Large>Thinking and searching the web...</Text.Large>
        <Spinner />
        <LoadingStateThinking />
      </FlexColumn>
    );
  }

  if (!data?.text) {
    return (
      <Box sx={aiDisplayStyle}>
        No additional information found on this project at the moment.
      </Box>
    );
  }

  return (
    <>
      <Box sx={aiDisplayStyle}>
        <Table sx={tableStyle}>
          <Section
            title="Analysis"
            content={data.text}
            shouldStream={shouldStartStreaming}
          />
        </Table>
      </Box>
      <Text.Small color={Colors.TextQuiet}>
        This content was generated by AI and should be independently verified for
        accuracy.
      </Text.Small>
    </>
  );
};

export default RequestDiplayGatorBot;
